import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { scrollTo } from "@/hooks/useSmoothScroll";
import heroVideo from "@/assets/hero-video.mp4";

gsap.registerPlugin(ScrollTrigger);

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const eyebrowRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const panel = panelRef.current;
    const eyebrow = eyebrowRef.current;
    const title = titleRef.current;
    const desc = descRef.current;
    const cta = ctaRef.current;
    if (!section || !panel || !eyebrow || !title || !desc || !cta) return;

    const titleLines = title.querySelectorAll("[data-line]");
    const ctaLines = cta.querySelectorAll("[data-line]");

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let entranceTl: gsap.core.Timeline | null = null;

    if (prefersReducedMotion) {
      gsap.set([panel, eyebrow, titleLines, desc, ctaLines], {
        opacity: 1,
        y: 0,
      });
    } else {
      entranceTl = gsap.timeline({ delay: 0.2 });

      entranceTl.fromTo(
        panel,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
        0
      );

      entranceTl.fromTo(
        eyebrow,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
        0.05
      );

      entranceTl.fromTo(
        titleLines,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.06,
          ease: "power3.out",
        },
        0.12
      );

      entranceTl.fromTo(
        desc,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
        0.24
      );

      entranceTl.fromTo(
        ctaLines,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.06,
          ease: "power3.out",
        },
        0.36
      );
    }

    const parallaxTween = gsap.to(videoContainerRef.current, {
      y: 150,
      ease: "none",
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "bottom top",
        scrub: true,
      },
    });

    return () => {
      entranceTl?.scrollTrigger?.kill();
      entranceTl?.kill();
      parallaxTween.scrollTrigger?.kill();
      parallaxTween.kill();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const handleScrollDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const section = sectionRef.current;
    const target = section?.nextElementSibling as HTMLElement | null;
    const y = target
      ? target.getBoundingClientRect().top + window.scrollY
      : window.innerHeight;
    scrollTo(y, { offset: 0, duration: 1.5 });
  };

  return (
    <section
      ref={sectionRef}
      className="relative h-screen min-h-[600px] overflow-hidden"
    >
      <div
        ref={videoContainerRef}
        className="absolute inset-0 will-change-transform bg-[#0D1F12]"
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover scale-110"
        >
          <source
            src={heroVideo}
            type="video/mp4"
          />
          <track kind="captions" srcLang="de" label="Deutsch" />
        </video>
        <div className="absolute inset-0 bg-black/[0.25]" />
      </div>

      <div className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-6 md:px-10 lg:px-14 pt-24 md:pt-28 pb-12 md:pb-16">
        {/* Glassmorphism Panel */}
        <div
          ref={panelRef}
          className="w-full md:w-[500px] lg:w-[520px] xl:w-[500px] 2xl:w-[500px] max-w-full max-h-[52vh] md:max-h-none overflow-visible p-4 sm:p-5 md:p-7 lg:p-8 xl:p-7 pb-7 sm:pb-8 md:pb-10 lg:pb-11 xl:pb-10 rounded-2xl backdrop-blur-[16px] border border-[#4CAF50]/25"
          style={{ backgroundColor: "rgba(13,31,18,0.45)" }}
        >
          {/* Eyebrow */}
          <div ref={eyebrowRef} className="mb-2 md:mb-3">
            <p className="text-[#4CAF50] text-[10px] md:text-xs lg:text-sm xl:text-sm font-heading uppercase tracking-widest">
              ELEKTRO-MEISTERBETRIEB · EMMERICH AM RHEIN ·
            </p>
          </div>

          {/* Headline */}
          <div ref={titleRef}>
            <h1 className="font-heading font-bold uppercase text-[22px] sm:text-[26px] md:text-[36px] lg:text-[44px] xl:text-[40px] text-white tracking-tight leading-[0.95] mt-2 md:mt-0">
              <span data-line className="block overflow-hidden">
                <span className="block whitespace-nowrap">Ihr Zuhause.</span>
              </span>
              <span data-line className="block overflow-hidden">
                <span className="block whitespace-nowrap text-accent-500">Ihre Energie.</span>
              </span>
            </h1>
          </div>

          {/* Description */}
          <div ref={descRef} className="mt-3 md:mt-4 max-w-md">
            <p className="text-white/80 text-xs sm:text-sm md:text-base lg:text-base font-body font-normal leading-relaxed whitespace-pre-line">
              Photovoltaik, Speicher, Wärme und Ladetechnik —{"\u00a0"}{"\n"}
              geplant, gebaut und betreut aus einer Hand.
            </p>
          </div>

          {/* CTAs */}
          <div ref={ctaRef} className="flex flex-wrap items-center gap-2 md:gap-3 lg:gap-4 mt-4 md:mt-7 lg:mt-8">
            <span data-line className="block overflow-hidden py-0.5 -my-0.5">
              <a
                href="/kontakt"
                className="inline-flex items-center px-4 py-2 md:px-5 md:py-3 lg:px-6 lg:py-3.5 rounded-full bg-accent-500 text-white font-body font-medium text-[11px] sm:text-xs hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                Kostenlose Erstberatung
              </a>
            </span>
            <span data-line className="block overflow-hidden py-0.5 -my-0.5">
              <a
                href="/kontakt"
                className="inline-flex items-center px-4 py-2 md:px-5 md:py-3 lg:px-6 lg:py-3.5 rounded-full border border-white text-white font-body font-medium text-[11px] sm:text-xs hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                So gehen wir vor
              </a>
            </span>
            <span data-line className="block overflow-hidden ml-auto">
              <a
                href="#"
                onClick={handleScrollDown}
                aria-label="Eine Sektion nach unten scrollen"
                className="group relative hidden md:flex w-10 h-10 md:w-10 md:h-10 lg:w-11 lg:h-11 rounded-full border border-white/40 items-center justify-center overflow-hidden transition-colors"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"
                />
                <i className="ri-arrow-down-line w-5 h-5 flex items-center justify-center relative z-10 text-white group-hover:text-foreground-950 transition-colors duration-300" />
              </a>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}